import { last } from 'underscore';
import avatar from '@/assets/flo/avatar.png';
import ResizableTextarea from '@/components/ResizableTextarea';
import Constants from '@/Constants';
import { DialogFlowMessage } from '@/models/Chat';
import { ApplicationState } from '@/store';
import { bufferingMessage, closeChatModal, sendChatMessage } from '@/store/bot/actions';
import { ChatMessage } from '@/store/bot/types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ChatContent from './ChatContent';
import ChatContentEmpty from './ChatContentEmpty';
import ChatHeader from './ChatHeader';
import './style.scss';
import { PayloadType } from './types';

const getPosition = (children: HTMLCollection) => {
    let position = 0;
    let index = children.length - 1;
    let last = children[index];

    while (last && !last.classList.contains('bot-message')) {
        if (!last.classList.contains('avatar-flo')) {
            position += last.getBoundingClientRect().height;
        }
        last = children[--index];
    }

    return position;
};

const isInteractiveMessage = (msg: DialogFlowMessage): boolean => {
    return msg && msg.payload && msg.payload.richContent[0][0].type === PayloadType.CHIPS;
};

const ChatModal: React.FC = () => {
    const { strings, person, chat, openedChat } = useSelector((state: ApplicationState) => ({
        person: state.identity.data.person,
        strings: state.locale.data,
        chat: {
            messages: state.chat.data.messages,
            buffering: state.chat.data.buffering,
            loading: state.chat.loading,
            hasError: state.chat.hasError,
        },
        openedChat: state.chat.openedChat,
    }));

    const [positionY, setPositionY] = useState<number>(-48);
    const [inputText, setInputText] = useState<string>('');

    const modalRef = useRef<HTMLInputElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    const dispatch = useDispatch();

    const [total, progress] = chat.buffering;
    const hasPendingMessages: boolean = progress < total;

    const lastMessage: DialogFlowMessage = last(chat.messages) as DialogFlowMessage;

    const disabledMessage = hasPendingMessages || isInteractiveMessage(lastMessage);
    const disabledMessageButton = disabledMessage || inputText.trim().length === 0;

    const sendMessage = (data: ChatMessage) => dispatch(sendChatMessage(data));

    const handleClickOutside = (event: any) => {
        if (modalRef && modalRef.current && !modalRef.current.contains(event.target)) {
            dispatch(closeChatModal());
        }
    };

    const handleSubmit = () => {
        sendMessage({ message: inputText, isAnswer: false });
        setInputText('');
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            dispatch(closeChatModal());
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        let positionY = 0;
        const chatMessages: HTMLDivElement | null = document.querySelector('.chat-messages');

        if (chatMessages) {
            const { height } = chatMessages.getBoundingClientRect();
            positionY = height - getPosition(chatMessages.children);
            setPositionY(positionY);
        }

        const scrollComponent = scrollRef.current;
        scrollComponent && (scrollComponent.scrollTop = scrollComponent.scrollHeight);

        hasPendingMessages && setTimeout(() => dispatch(bufferingMessage()), 1000);
    }, [chat.messages, chat.buffering]);

    return (
        <div className={`chat-modal ${!openedChat ? 'chat-modal-closed' : ''}`} ref={modalRef}>
            <ChatHeader avatar={avatar} strings={strings} />
            <div className="chat-modal-content" key="chat-modal-content">
                {chat.messages.length == 0 ? (
                    <ChatContentEmpty avatar={avatar} person={person} strings={strings} />
                ) : (
                    <div className="chat-content-scroll" ref={scrollRef}>
                        <ChatContent
                            avatar={avatar}
                            buffering={chat.buffering}
                            chat={chat}
                            person={person}
                            positionY={positionY}
                            sendMessage={sendMessage}
                            strings={strings}
                        />
                    </div>
                )}
            </div>
            <div className="chat-modal-footer">
                <div className="text-input">
                    <ResizableTextarea
                        disabled={disabledMessage}
                        maxLength={255}
                        maxRows={4}
                        onSubmit={handleSubmit}
                        placeholder={strings.BOT_TYPE_YOUR_QUESTION}
                        setValue={setInputText}
                        submitOnEnter
                        value={inputText}
                    />
                </div>
                <button className="submit-button" disabled={disabledMessageButton} onClick={handleSubmit} type="button">
                    <i className="close-button material-icons">{Constants.icons.SEND}</i>
                </button>
            </div>
        </div>
    );
};

export default ChatModal;
